import React, { useState } from 'react';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { trackNewsletterSignup } from '../utils/analytics';
import './NewsletterSignup.css';
import Icon from './icons/Icon';

const NewsletterSignup = ({ variant = 'default' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // 'success', 'error', 'duplicate'
  const [loading, setLoading] = useState(false);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setStatus('error');
      return;
    }

    if (!validateEmail(email)) {
      setStatus('error');
      return;
    }

    try {
      setLoading(true);
      setStatus(null);

      if (!db) {
        throw new Error('Database not initialized');
      }

      const normalizedEmail = email.toLowerCase().trim();
      const subscriberRef = doc(db, 'newsletter', normalizedEmail);
      const existingSnap = await getDoc(subscriberRef);

      if (existingSnap.exists()) {
        setStatus('duplicate');
        setEmail('');
        setTimeout(() => setStatus(null), 5000);
        return;
      }

      await setDoc(subscriberRef, {
        email: normalizedEmail,
        subscribedAt: serverTimestamp(),
        source: 'website',
        active: true,
      });

      trackNewsletterSignup();

      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus(null), 5000);
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      setStatus('error');
      setTimeout(() => setStatus(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const containerClass = `newsletter-signup newsletter-signup--${variant}`;

  return (
    <div className={containerClass}>
      <form onSubmit={handleSubmit} className="newsletter-form">
        <div className="newsletter-header">
          <h3 className="newsletter-title"><Icon name="inbox" size={20} /> Stay Updated</h3>
          <p className="newsletter-description">
            Get the latest dental care tips, health insights, and exclusive offers delivered to your inbox.
          </p>
        </div>

        <div className="newsletter-input-group">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            className="newsletter-input"
            required
            disabled={loading}
            aria-label="Email address for newsletter subscription"
          />
          <button
            type="submit"
            className="newsletter-button"
            disabled={loading}
            aria-label="Subscribe to newsletter"
          >
            {loading ? 'Subscribing...' : 'Subscribe'}
          </button>
        </div>

        {status === 'success' && (
          <div className="newsletter-message newsletter-message--success" role="alert">
            <Icon name="check" size={16} /> Thank you for subscribing! Check your email for confirmation.
          </div>
        )}

        {status === 'duplicate' && (
          <div className="newsletter-message newsletter-message--info" role="alert">
            <Icon name="info" size={16} /> This email is already subscribed. Thank you!
          </div>
        )}

        {status === 'error' && (
          <div className="newsletter-message newsletter-message--error" role="alert">
            <Icon name="x" size={16} /> Something went wrong. Please try again.
          </div>
        )}

        <p className="newsletter-privacy">
          We respect your privacy. Unsubscribe at any time.
        </p>
      </form>
    </div>
  );
};

export default NewsletterSignup;
